import {
  DEAL_QUEUE_GROUPS,
  DEAL_QUEUE_SORTS,
  type DealQueueAuction,
  type DealQueueClient,
  type DealQueueGroup,
  type DealQueueSort,
  type DealQueueTab,
  dealGroupOf,
} from '@carsfinders/contracts';
import { type KeyboardEvent, useRef } from 'react';

import { t } from '../../../lib/i18n';
import { cn } from '../../bids/utils';

interface DealsTabsProps {
  /** The URL's tab: a group («Активні» / «Завершені» / «Архів») or one stage inside it. */
  active: DealQueueTab;
  /** Per-tab counters from the queue response; null while the first page loads. */
  counts: Partial<Record<DealQueueTab, number>> | null;
  onChange: (tab: DealQueueTab) => void;
  /** id of the panel the tabs control, for `aria-controls`. */
  panelId: string;
  sort: DealQueueSort;
  onSortChange: (sort: DealQueueSort) => void;
  /**
   * The «Клієнт» filter — only on «Угоди»; the client page is already one
   * client. `clientId` '' = all clients.
   */
  clientFilter?: {
    clients: readonly DealQueueClient[];
    clientId: string;
    onChange: (clientId: string) => void;
  };
  /**
   * The «Аукціон» filter (owner, 2026-10-13: «фільтр по аукціонах») — only on
   * «Угоди». `auction` '' = all auctions.
   */
  auctionFilter?: {
    auctions: readonly DealQueueAuction[];
    auction: string;
    onChange: (auction: string) => void;
  };
}

const GROUPS: readonly DealQueueGroup[] = ['active', 'finished', 'archive'];

/** The tab id the panel is labelled by — a stage is shown under its group's tab. */
export function dealsTabId(tab: DealQueueTab): string {
  return `deals-tab-${dealGroupOf(tab) ?? 'active'}`;
}

/**
 * «Активні · Завершені · Архів» plus the «Етап» filter (owner, 2026-10-12: «а
 * решту винеси у фільтр»; 2026-10-25: «додати ще архівні»). «Активні» are the
 * lots whose real auction is still being played — Ретрансляція and Лідирує;
 * «Завершені» is what still waits on the operator after it (the outcome, an
 * invoice, a payment, a delivery); «Архів» is every lot with a final status. The eight narrow tabs the
 * screen used to show are the filter's options, inside the group they belong
 * to, read from the contract's `DEAL_QUEUE_GROUPS`, never a local copy.
 *
 * The three tabs are a real ARIA tablist: one tab in the tab order (roving
 * `tabIndex`), ←/→ move between them and select on arrival.
 */
export function DealsTabs({
  active,
  counts,
  onChange,
  panelId,
  sort,
  onSortChange,
  clientFilter,
  auctionFilter,
}: DealsTabsProps) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const group = dealGroupOf(active) ?? 'active';

  const move = (index: number) => {
    const next = (index + GROUPS.length) % GROUPS.length;
    const tab = GROUPS[next];
    if (!tab) return;
    onChange(tab);
    refs.current[next]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        move(index + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        move(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        move(0);
        break;
      case 'End':
        event.preventDefault();
        move(GROUPS.length - 1);
        break;
      default:
        break;
    }
  };

  const withCount = (tab: DealQueueTab, label: string): string => {
    const count = counts?.[tab];
    return count === undefined ? label : `${label} · ${count}`;
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div
        role="tablist"
        aria-label={t('admin.deals.tabsAria')}
        className="flex w-fit gap-0.5 rounded-cf-lg bg-cf-fill p-[3px]"
      >
        {GROUPS.map((tab, index) => {
          const isActive = tab === group;
          const count = counts?.[tab];
          return (
            <button
              key={tab}
              ref={(element) => {
                refs.current[index] = element;
              }}
              id={`deals-tab-${tab}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onChange(tab)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                'h-9 shrink-0 rounded-cf-md px-3.5 text-[13px] font-semibold whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-brand',
                isActive ? 'bg-cf-brand text-white' : 'bg-transparent text-cf-ink',
              )}
            >
              {t(`admin.deals.tabs.${tab}`)}
              {count !== undefined && (
                <>
                  {' '}
                  <span className="font-mono text-[11px] font-medium opacity-70">{count}</span>
                </>
              )}
            </button>
          );
        })}
      </div>

      <label className="flex h-9 items-center gap-2 text-[13px] text-cf-ink-muted">
        {t('admin.deals.stageFilter')}
        <select
          value={active === group ? group : active}
          onChange={(event) => onChange(event.target.value as DealQueueTab)}
          data-testid="deals-stage-filter"
          className="h-9 rounded-cf-md border border-cf-line bg-cf-surface px-2.5 text-[13px] text-cf-ink focus-visible:outline-2 focus-visible:outline-cf-brand"
        >
          <option value={group}>{withCount(group, t('admin.deals.stageAll'))}</option>
          {DEAL_QUEUE_GROUPS[group].map((tab) => (
            <option key={tab} value={tab}>
              {withCount(tab, t(`admin.deals.tabs.${tab}`))}
            </option>
          ))}
        </select>
      </label>

      {clientFilter && (
        <label className="flex h-9 items-center gap-2 text-[13px] text-cf-ink-muted">
          {t('admin.deals.clientFilter')}
          <select
            value={clientFilter.clientId}
            onChange={(event) => clientFilter.onChange(event.target.value)}
            data-testid="deals-client-filter"
            className="h-9 max-w-[220px] rounded-cf-md border border-cf-line bg-cf-surface px-2.5 text-[13px] text-cf-ink focus-visible:outline-2 focus-visible:outline-cf-brand"
          >
            <option value="">{t('admin.deals.clientAll')}</option>
            {/* A client named in the URL but out of the list (no lots under this
                search) still shows as picked rather than silently reading «all». */}
            {clientFilter.clientId &&
              !clientFilter.clients.some((client) => client.id === clientFilter.clientId) && (
                <option value={clientFilter.clientId}>{t('admin.deals.clientPicked')}</option>
              )}
            {clientFilter.clients.map((client) => (
              <option key={client.id} value={client.id}>
                {`${client.name} · ${client.count}`}
              </option>
            ))}
          </select>
        </label>
      )}

      {auctionFilter && (
        <label className="flex h-9 items-center gap-2 text-[13px] text-cf-ink-muted">
          {t('admin.deals.auctionFilter')}
          <select
            value={auctionFilter.auction}
            onChange={(event) => auctionFilter.onChange(event.target.value)}
            data-testid="deals-auction-filter"
            className="h-9 max-w-[180px] rounded-cf-md border border-cf-line bg-cf-surface px-2.5 text-[13px] text-cf-ink focus-visible:outline-2 focus-visible:outline-cf-brand"
          >
            <option value="">{t('admin.deals.auctionAll')}</option>
            {/* An auction named in the URL but out of the list still reads as picked. */}
            {auctionFilter.auction &&
              !auctionFilter.auctions.some((item) => item.name === auctionFilter.auction) && (
                <option value={auctionFilter.auction}>{auctionFilter.auction}</option>
              )}
            {auctionFilter.auctions.map((item) => (
              <option key={item.name} value={item.name}>
                {`${item.name} · ${item.count}`}
              </option>
            ))}
          </select>
        </label>
      )}

      {/* «Закінчуються скоро ▾» — the client «Мої ставки» order, plus the work queue. */}
      <label className="flex h-9 items-center gap-2 text-[13px] text-cf-ink-muted">
        {t('admin.deals.sortLabel')}
        <select
          value={sort}
          onChange={(event) => onSortChange(event.target.value as DealQueueSort)}
          data-testid="deals-sort"
          className="h-9 rounded-cf-md border border-cf-line bg-cf-surface px-2.5 text-[13px] text-cf-ink focus-visible:outline-2 focus-visible:outline-cf-brand"
        >
          {DEAL_QUEUE_SORTS.map((option) => (
            <option key={option} value={option}>
              {t(`admin.deals.sort.${option}`)}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
